/**
 * Whether the composer's attach panel may offer **Take Photo**.
 *
 * Both phones offer it unconditionally (iOS gates on `cameraAvailable`, Android always shows it),
 * because a phone always has a camera and a file picker that is not one. A browser is not in that
 * position, so the row is offered only where a camera is actually within reach: a **coarse pointer**,
 * i.e. a touch device.
 *
 * This is deliberately not a width breakpoint. A laptop narrowed to 500px has no camera roll and
 * `capture` does nothing there, while a tablet at 1100px does — the media query asks the question we
 * actually mean, and it is how the row ends up on mobile web and nowhere else.
 *
 * ⚠️ **It deliberately does NOT sniff for `capture` support**, and that omission is the considered
 * part. The obvious test — `'capture' in document.createElement('input')` — is the IDL attribute,
 * which Blink ships only on Android: it is **false in desktop Chrome even under full mobile
 * emulation** (measured), and whether WebKit exposes it on iOS is not something we can confirm from
 * a build machine. A gate that answers "no" on iOS Safari would ship this row dead on the largest
 * mobile-web browser there is, and nothing would fail — the row would simply never appear.
 *
 * Dropping the sniff costs nothing, because the degradation is graceful rather than dishonest. Where
 * `capture` IS honoured the row opens the camera directly, which is the point. Where it is ignored
 * the browser falls back to its ordinary picker — and on a phone that picker is the OS sheet whose
 * own first entries are Take Photo / Photo Library, so the user still reaches a camera, one tap
 * further on. That is the difference between this row and the Photo Library row we deliberately do
 * NOT offer: this one's worst case still gets you to a camera.
 *
 * Takes the window so it is testable without jsdom (jest runs on `node` here) and so SSR — where
 * there is no window at all — resolves to `false` rather than throwing. A first paint without the
 * row that gains it on hydration is the right way round: offering a row that cannot work and then
 * removing it is the failure that matters.
 */
export function cameraCaptureSupported(win: Pick<Window, 'matchMedia'> | undefined): boolean {
  if (!win || typeof win.matchMedia !== 'function') return false;
  try {
    return win.matchMedia('(pointer: coarse)').matches;
  } catch {
    // A browser too old to parse the query is a browser too old to be handed a camera row.
    return false;
  }
}
